import React, { useState, useRef, useEffect } from 'react';
import { Fragment } from "react";
import { Dialog, Transition } from '@headlessui/react';
import { AiOutlineSetting } from 'react-icons/ai';
import { FaRobot } from 'react-icons/fa';
import { AssistantManager } from './AssistantManager/AssistantManager';
import { AssistantCreator } from './AssistantCreator/AssistantCreator';
import { getToken } from '../../authConfig';
import { useMsal } from '@azure/msal-react';
import {
    checkGroupMembership,
    getUserId,
    resolveEntities
} from '../../api/api';
interface AssistantDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    defaultTab: "manage" | "create";
}

export const AssistantDialog = ({ open, setOpen, defaultTab }: AssistantDialogProps) => {
    const [activeTab, setActiveTab] = useState<"manage" | "create">(defaultTab);
    const cancelButtonRef = useRef(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const { instance: client } = useMsal();

    useEffect(() => {
        if (open) {
            setActiveTab(defaultTab);
        }
    }, [open, defaultTab]);

    //checking if current user is a create custom assistant admin
    useEffect(() => {
        const checkAdmin = async () => {
            const token = await getToken(client);
            const isMember = await checkGroupMembership(
                "b5e74d80-ee44-4310-86f6-91bbcfabb371",
                token?.accessToken
            );
            setIsAdmin(isMember);

            console.log("Checked admin status:", isMember);
            
        };
        checkAdmin();
    }, []);

    const closeDialog = () => {
        setOpen(false);
    };

    const tabsData = [
        {
            label: "Manage Assistants",
            value: "manage" as "manage",
            icon: AiOutlineSetting,
            content: (
                <AssistantManager
                    onCancel={closeDialog}
                />
            ),
        },
        {
            label: "Create Custom Assistant",
            value: "create" as "create",
            icon: FaRobot,
            content: (
                <AssistantCreator
                    onSubmit={closeDialog}
                    onCancel={closeDialog}
                />
            ),
        },
    ];

    const filteredTabs = tabsData.filter(tab => {
        if (tab.value === "create") return isAdmin;
        return true;
    });

    return (
        <Transition show={open} as={Fragment}>
            <Dialog
                className="relative z-[100]"
                initialFocus={cancelButtonRef}
                onClose={() => setOpen(false)}
            >
                {/* Overlay */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-eq-grey bg-opacity-50 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="w-full sm:max-w-7xl h-[90vh] flex flex-col transform overflow-hidden rounded-sm bg-eq-white text-left align-middle shadow-2xl transition-all">
                                <div className="flex-1 flex flex-col border border-eq-light-grey rounded-sm overflow-hidden">
                                    {/* Mobile Dropdown */}
                                    <div className="sm:hidden relative">
                                        <select
                                            value={activeTab}
                                            onChange={(e) => setActiveTab(e.target.value as "create" | "manage")}
                                            className="block appearance-none w-full bg-eq-white border border-eq-grey text-eq-grey text-lg rounded-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-2 focus:ring-eq-green focus:border-eq-green dark:bg-eq-grey dark:text-white"
                                        >
                                            {filteredTabs.map(({ label, value }) => (
                                                <option key={value} value={value}>
                                                    {label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                            <svg
                                                className="h-5 w-5 text-eq-grey"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.586l3.71-4.354a.75.75 0 111.14.976l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </div>
                                    </div>

                                    {/* Desktop Tabs */}
                                    <ul className="hidden text-sm font-medium text-center text-eq-grey rounded-sm sm:flex">
                                        {filteredTabs.map(({ label, value, icon: Icon }) => (
                                            <li key={value} className="w-full focus-within:z-10">
                                                <button
                                                    onClick={() => setActiveTab(value)}
                                                    className={`inline-block w-full p-4 border-r border-eq-light-grey focus:outline-none ${activeTab === value
                                                        ? 'bg-eq-white text-eq-grey'
                                                        : 'bg-eq-mid-grey text-eq-grey hover:bg-eq-grey hover:text-eq-white'
                                                        }`}
                                                >
                                                    <div className="flex items-center justify-center gap-2 text-lg">
                                                        <Icon className="w-6 h-6" />
                                                        {label}
                                                    </div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* Tab Content */}
                                    <div className="flex-1 overflow-y-auto">
                                        {filteredTabs.find(tab => tab.value === activeTab)?.content}
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
